import React, { useContext, useState } from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { AsYouType } from 'libphonenumber-js'
import { useForm, useWatch } from 'react-hook-form'
import { useRoute } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { usStatesSelector, caProvincesSelector, userSelector } from '@src/selectors/app'
import { useGoBack, useDispatchAsync } from '@src/utils'
import { NavigatorContext } from '@src/utils/navigatorContext'
import { NavigationContainer } from '@src/screens/Common/containers'
import { TransparentNavigationBar } from '@src/components/navigationBar/TransparentNavigationBar'
import { AddressCountries, UiInlineAlert } from '@src/types'
import { CommonAuthorizedRouteProps } from '@src/navigation/types'
import { AddressType } from '@src/screens/Marketplace/components/checkout/AddressForm'
import { getCountryCodeByCountry } from '@src/utils/phoneInput'
import { AddressForm, AddressFormData } from '@src/components/Address/AddressForm'
import { InlineAlert, ScrollableAvoidKeyboard } from '@src/components'
import { Button } from '@src/components/base'
import { useSecureScreen } from '@src/hooks/useSecureScreen'

export const AddressModalContainer = () => {
  useSecureScreen()

  const dispatch = useDispatchAsync()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const insets = useSafeAreaInsets()

  const user = useSelector(userSelector)
  const usStates = useSelector(usStatesSelector)
  const caProvinces = useSelector(caProvincesSelector)

  const route = useRoute<CommonAuthorizedRouteProps<'ChangeAddress'>>()

  const addressType = route.params.addressType

  const address = user?.address

  const styles = useStyleSheet(themedStyle)
  const goBack = useGoBack()

  const defaultCountry = (address?.country as AddressCountries | undefined) || AddressCountries.Us

  const defaultValues: AddressFormData = {
    country: defaultCountry,
    name: address?.name || `${user?.firstName} ${user?.lastName}`,
    street: address?.street || '',
    street2: address?.street2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zipCode: address?.zipCode || '',
    phoneNumber: new AsYouType(getCountryCodeByCountry(defaultCountry)).input(
      user?.phoneNumber || '',
    ),
  }

  const form = useForm({ mode: 'onTouched', defaultValues })
  const {
    handleSubmit,
    formState: { isValid },
    control,
  } = form

  const country = useWatch({ control, name: 'country' })
  const state = useWatch({ control, name: 'state' })

  const states = (country === AddressCountries.Us ? usStates : caProvinces).map((item) => ({
    text: item.abbreviation,
    value: item.abbreviation,
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async ({ phoneNumber, ...address }: AddressFormData) => {
    if (isSubmitting) {
      return
    }

    const stateFromServer = states.find((item) => item.text.toLowerCase() === state.toLowerCase())

    setIsSubmitting(true)
    enableLoader()
    try {
      // we're refetching user data after creating address for the first time
      // so stale user fetch might override the phone number update
      // hence we update phone number first
      if (phoneNumber) {
        await dispatch({ type: 'users/update', payload: { phoneNumber } })
      }

      await dispatch({
        type: 'address/update',
        payload: {
          ...address,
          state: stateFromServer?.value,
        },
      })

      goBack()
    } finally {
      disableLoader()
      setIsSubmitting(false)
    }
  }

  const title = addressType === AddressType.Shipping ? 'Shipping Address' : 'Billing Address'

  const allowCountryChange = defaultValues.country !== AddressCountries.Us
  const showShippingAddressAlert = addressType === AddressType.Shipping

  return (
    <NavigationContainer
      title={title}
      goBack={goBack}
      NavigationBarComponent={TransparentNavigationBar}
      style={styles.container}
    >
      <ScrollableAvoidKeyboard
        style={styles.container}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        bounces={false}
      >
        <AddressForm showCountry allowCountryChange={allowCountryChange} form={form} />
        <View style={styles.footer}>
          {showShippingAddressAlert && (
            <InlineAlert
              style={styles.alert}
              category={UiInlineAlert.Info}
              message={
                'Please verify that your shipping address is accurate' +
                ' and current prior to your payment method being charged. ' +
                `Failure to do so will result in your product being shipped to the address on file at the time of billing.`
              }
            />
          )}
          <Button
            type="primary"
            size="block"
            disabled={!isValid}
            style={styles.button}
            onPress={handleSubmit(onSubmit)}
            accessibilityLabel="Save"
            allowPressWhenDisabled
          >
            Save
          </Button>
        </View>
      </ScrollableAvoidKeyboard>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    flexGrow: 1,
    flexShrink: 0,
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  footer: {
    marginTop: 'auto',
  },
  alert: {
    marginTop: 16,
  },
  button: {
    marginTop: 24,
  },
})
